import { useCallback, useMemo } from 'react';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { DragDropList } from '@/components/ui/custom/dataInput/DragDrop';
import { Loading } from '@/components/ui/daisy';
import useLessonItemsMap from '@/data/hook/lesson-plan/useLessonItemsMap';
import useLessonItemsSummary from '@/data/hook/lesson-plan/useLessonItemsSummary';
import useLessonPlans from '@/data/hook/lesson-plan/useLessonPlans';
import useReorderLessonPlans from '@/data/hook/lesson-plan/useReorderLessonPlans';
import useNestedOptimisticReorder from '@/data/hook/useNestedOptimisticReorder';
import CoursePath from '@/models/CoursePath';
import LessonPlan from '@/models/LessonPlan';
import LessonPlanItem from '@/models/LessonPlanItem';
import { adapterLessonPlanOrder } from '@/utils/lesson-plan/adapterLessonPlanOrder';

import mapToObject from '@/utils/mapToObject';
import LessonPlanCard from './LessonPlanCard';
import LessonPlanFormList from './LessonPlanFormList';
import LessonPlanItemList from './LessonPlanItemList';
import LessonPlansSummaryBadges from './LessonPlansSummaryBadges';

export enum LessonPlanDndType {
  LESSON_PLAN = 'LESSON_PLAN',
  LESSON_PLAN_ITEM = 'LESSON_PLAN_ITEM',
}

const EMPTY_ITEMS: LessonPlanItem[] = [];

type LessonPlansTabContentProps = {
  coursePath: CoursePath;
  courseLineId: number;
};

export default function LessonPlansTabContent({
  coursePath,
  courseLineId,
}: LessonPlansTabContentProps) {
  const coursePathId = coursePath.id;
  const {
    lessonPlans,
    cacheManager: lessonPlanCache,
    isLoading,
    createLessonPlan,
    isCreating,
    updateLessonPlan,
    isUpdating,
    deleteLessonPlan,
    isDeleting,
  } = useLessonPlans({ coursePathId });

  const lessonPlanIds = useMemo(
    () => lessonPlans.map(lessonPlan => lessonPlan.id),
    [lessonPlans],
  );

  const {
    itemsMap,
    cacheManager: itemsCache,
    createLessonPlanItem,
    isCreating: isCreatingItem,
    updateLessonPlanItem,
    isUpdating: isUpdatingItem,
    deleteLessonPlanItem,
    isDeleting: isDeletingItem,
  } = useLessonItemsMap({ lessonPlanIds });

  const { totalTime, getTimeByLessonPlan } = useLessonItemsSummary({
    itemsMap,
  });

  const { reorderLessonPlans, isReordering } = useReorderLessonPlans({
    itemsCache,
    lessonPlanCache,
  });

  const handleReorder = useCallback(
    (
      reorderedParents: LessonPlan[],
      reorderedChildrenMap: Record<string, LessonPlanItem[]>,
    ) => {
      const order = adapterLessonPlanOrder(
        reorderedParents,
        reorderedChildrenMap,
      );

      reorderLessonPlans(order);
    },
    [reorderLessonPlans],
  );

  const {
    optimisticParents,
    optimisticChildrenMap,
    handleDragEnd,
    isDraggingParent,
    onBeforeCapture,
  } = useNestedOptimisticReorder({
    parents: lessonPlans,
    childrenMap: mapToObject(itemsMap),
    parentType: LessonPlanDndType.LESSON_PLAN,
    parentPrefix: 'lessonPlan',
    isReordering,
    onReorder: handleReorder,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading variant="spinner" size="lg" color="primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <ConditionalRenderer condition={lessonPlans.length > 0}>
        <LessonPlansSummaryBadges
          lessonsCount={lessonPlans.length}
          totalMinutes={totalTime}
        />
      </ConditionalRenderer>
      <div className="relative">
        <ConditionalRenderer condition={lessonPlans.length > 0}>
          <DragDropList
            onDragEnd={handleDragEnd}
            onBeforeCapture={onBeforeCapture}
            groupId="lessonPlans"
            type={LessonPlanDndType.LESSON_PLAN}
            customPlaceholder
            className="gap-0"
          >
            {optimisticParents.map((lessonPlan, index) => (
              <LessonPlanCard
                key={lessonPlan.id}
                lessonPlan={lessonPlan}
                index={index}
                isDraggable
                isDragging={isDraggingParent}
                isReordering={isReordering}
                time={getTimeByLessonPlan(lessonPlan.id)}
                onUpdate={updateLessonPlan}
                isUpdating={isUpdating}
                isDeleting={isDeleting}
                onDelete={deleteLessonPlan}
              >
                <LessonPlanItemList
                  lessonPlanId={lessonPlan.id}
                  items={
                    optimisticChildrenMap[String(lessonPlan.id)] ?? EMPTY_ITEMS
                  }
                  isReordering={isReordering}
                  coursePathId={coursePathId}
                  courseLineId={courseLineId}
                  onCreate={createLessonPlanItem}
                  isCreating={isCreatingItem}
                  onUpdate={updateLessonPlanItem}
                  isUpdating={isUpdatingItem}
                  isDeleting={isDeletingItem}
                  onDelete={deleteLessonPlanItem}
                />
              </LessonPlanCard>
            ))}
          </DragDropList>
        </ConditionalRenderer>
        <LessonPlanFormList
          onSubmit={createLessonPlan}
          isLoading={isCreating}
        />
      </div>
    </div>
  );
}
